import { useState } from 'react';
import { Modal, Progress, Typography, Button, Space, message } from 'antd';
import { excelService, type ExcelExportJobResponse } from '@/services/excel.service';
import { downloadExcelExportJob } from './excelDownload';

const DEFAULT_POLL_INTERVAL_MS = 1500;
// TCR-2500 — bỏ cắt 60s cứng: job Kho/Kế toán xử lý hàng chục nghìn dòng
// (22k+ stock_movements ở bản sao prod) nên có thể chạy hàng chục phút.
// Trần 30 phút chỉ là biên an toàn chống poll vô hạn; tới trần thì báo user
// xem ở lịch sử export, KHÔNG dừng job.
const DEFAULT_TIMEOUT_MS = 30 * 60 * 1000;
// Số lần poll lỗi liên tiếp (mạng/API chớp nhoáng) trước khi bỏ cuộc để
// không treo modal mãi khi BE chết hẳn.
const MAX_CONSECUTIVE_POLL_ERRORS = 5;

// TCR-2500 — nhãn pha theo hợp đồng progress.phase; khi BE chưa ghi progress
// (job cũ tạo trước TCR-2500) thì rơi về text processing chung.
const PHASE_LABELS: Record<string, string> = {
  collecting: 'Đang thu thập dữ liệu…',
  building: 'Đang tạo tệp Excel…',
  uploading: 'Đang tải tệp lên hệ thống…',
};

interface ProgressView {
  percent: number;
  text: string;
}

// Kênh đẩy tiến trình từ vòng poll (ngoài React) vào nội dung modal — dùng ref
// per-call thay vì modal.update() vì antd 5.15 static modal update không áp dụng
// title/okText/icon. `latest` giữ job mới nhất để component mount TRỄ vẫn hiển
// thị đúng trạng thái hiện tại (push đầu tiên có thể diễn ra trước khi React
// commit nội dung modal).
interface ProgressLiveRef {
  push?: (job: ExcelExportJobResponse) => void;
  latest?: ExcelExportJobResponse;
}

function LiveProgressContent({ initialJob, liveRef }: { initialJob: ExcelExportJobResponse; liveRef: ProgressLiveRef }) {
  const [view, setView] = useState<ProgressView>(() => describeProgress(liveRef.latest ?? initialJob));
  // Đăng ký ngay trong render (không qua useEffect) để không bỏ lỡ push nào.
  liveRef.push = (nextJob) => setView(describeProgress(nextJob));
  return (
    <div data-testid="excel-export-progress">
      <Progress percent={view.percent} status="active" />
      <div>{view.text}</div>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        Bấm "Huỷ chờ" để quay lại làm việc — job vẫn chạy nền, tải tệp ở lịch sử export.
      </Typography.Text>
    </div>
  );
}

// Nút tải trong modal thành công — loading state quản bằng React state riêng.
function DownloadButton({ onDownload }: { onDownload: () => Promise<boolean> }) {
  const [downloading, setDownloading] = useState(false);
  return (
    <Button
      type="primary"
      loading={downloading}
      onClick={async () => {
        setDownloading(true);
        try {
          await onDownload();
        } finally {
          setDownloading(false);
        }
      }}
    >
      Tải xuống
    </Button>
  );
}

export interface WaitForExportJobOptions {
  pollIntervalMs?: number;
  timeoutMs?: number;
  /** Bỏ qua — giữ để backward-compat với caller cũ (trước đây là key của notification). */
  notificationKey?: string;
}

/**
 * TCR-2500 đợt 2 (review wave 2, finding 7) — kết quả thật của helper.
 *
 * Trước đây helper resolve với `void` ở MỌI nhánh kết thúc (job lỗi, user huỷ
 * chờ, hết thời gian chờ, poll lỗi, tải file lỗi) → màn hình báo "Đã xuất file"
 * dù KHÔNG có file nào về máy. Nay phân biệt rõ:
 * - 'downloaded' → file ĐÃ tải xuống thành công (mới được báo thành công).
 * - 'cancelled'  → user bấm "Huỷ chờ" (job vẫn chạy nền, tải ở lịch sử export).
 * - 'failed'     → job lỗi, hoặc tải file lỗi / bị trình duyệt chặn (chưa có file).
 * - 'timeout'    → quá thời gian chờ (30 phút) → job vẫn chạy nền.
 *
 * Gọn nhẹ: caller cũ KHÔNG dùng giá trị trả về vẫn chạy y hệt (chỉ khác kiểu
 * `Promise<void>` → `Promise<ExportJobDownloadOutcome>`).
 */
export type ExportJobDownloadOutcome = 'downloaded' | 'cancelled' | 'failed' | 'timeout';

export async function waitForExportJobDownload(
  initialJob: ExcelExportJobResponse,
  options: WaitForExportJobOptions = {},
): Promise<ExportJobDownloadOutcome> {
  const pollIntervalMs = options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const startedAt = Date.now();
  let job = initialJob;
  // "Huỷ chờ" CHỈ dừng vòng poll ở FE — job vẫn chạy tiếp trên BE, user tải
  // kết quả ở lịch sử export. Nhãn nút và modal nói rõ điều này.
  let cancelled = false;
  let consecutivePollErrors = 0;
  const progressLive: ProgressLiveRef = {};
  const modal = Modal.info({
    title: 'Đang tạo tệp Excel',
    content: <LiveProgressContent initialJob={job} liveRef={progressLive} />,
    // "Huỷ chờ" là nút duy nhất: dừng poll, không huỷ job (BE không có API huỷ
    // job excel trong đợt 1 — SPEC mục KHÔNG làm).
    okText: 'Huỷ chờ',
    okButtonProps: { danger: true },
    onOk: () => {
      cancelled = true;
    },
    closable: false,
    keyboard: false,
    maskClosable: false,
  });

  // Vòng poll chạy đến khi job vào trạng thái cuối (completed/failed), user huỷ
  // chờ, quá trần 30 phút, hoặc poll lỗi liên tiếp (for(;;) + break/return).
  for (;;) {
    // Hoàn tất → thoát vòng poll, chuyển modal thành công ở dưới.
    if (job.status === 'completed') break;

    if (job.status === 'failed') {
      modal.destroy();
      // Lỗi hiện nguyên nhân từ BE (job.error) để user biết lý do thật.
      Modal.error({
        title: 'Tạo tệp Excel thất bại',
        content: (
          <div>
            {job.error ? <p>{job.error}</p> : <p>Hệ thống xử lý không thành công.</p>}
            <Typography.Text type="secondary">
              Vui lòng thử lại hoặc xem chi tiết ở lịch sử export. Mã job: {job.export_id}
            </Typography.Text>
          </div>
        ),
      });
      return 'failed';
    }

    if (cancelled) {
      modal.destroy();
      // Không dùng notification duration: 0 (SPEC cấm) — message tự tắt.
      message.info('Đã huỷ chờ. Job vẫn tiếp tục chạy nền — tải tệp ở lịch sử export khi xong.');
      return 'cancelled';
    }

    if (Date.now() - startedAt > timeoutMs) {
      modal.destroy();
      message.info('Job vẫn đang được xử lý. Vui lòng tải lại ở lịch sử export sau ít phút.');
      return 'timeout';
    }

    await sleep(pollIntervalMs);
    try {
      job = await excelService.getExportJob(initialJob.export_id);
      consecutivePollErrors = 0;
      if (!cancelled && job.status !== 'completed' && job.status !== 'failed') {
        // latest set TRƯỚC push: component mount trễ vẫn đọc được trạng thái mới nhất.
        progressLive.latest = job;
        progressLive.push?.(job);
      }
    } catch {
      consecutivePollErrors += 1;
      if (consecutivePollErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
        modal.destroy();
        message.error('Không kiểm tra được tiến trình export. Vui lòng xem ở lịch sử export.');
        // Bỏ cuộc theo dõi → KHÔNG có file nào tải về → 'failed' (không phải
        // 'timeout' vì không phải hết thời gian chờ).
        return 'failed';
      }
    }
  }

  // TCR-2500 fix UX (Chrome thật) — sau ~40s poll, window.open KHÔNG còn user
  // gesture nên Chrome chặn popup: modal biến mất, không tab/file, không báo lỗi.
  // Vì vậy khi job xong KHÔNG destroy + tự open âm thầm nữa — destroy modal chờ
  // và mở modal thành công riêng (Modal.success có sẵn icon xanh) với nút
  // "Tải xuống" (click của user → không bị chặn) và nút "Đóng". Modal chỉ đóng
  // khi user bấm "Đóng", hoặc bấm "Tải xuống" và tải thành công.
  async function tryDownload(closeOnSuccess: boolean): Promise<boolean> {
    let ok = false;
    try {
      const result = await downloadExcelExportJob(job);
      ok = result.ok;
    } catch {
      // Auto tải im lặng (không hiện lỗi sai khi popup bị chặn); lỗi chỉ báo
      // khi user chủ động bấm "Tải xuống".
      if (closeOnSuccess) {
        message.error('Tải tệp thất bại. Vui lòng tải lại ở lịch sử export.');
      }
    }
    if (ok && closeOnSuccess) {
      successModal.destroy();
      return true;
    }
    // Bị chặn (window.open trả null) hoặc lỗi → giữ nguyên modal + nút.
    return ok;
  }

  function renderSuccessContent() {
    const processed = job.progress?.processed;
    return (
      <div data-testid="excel-export-success">
        {typeof processed === 'number' && processed > 0 ? (
          <div>Đã xuất {processed} dòng</div>
        ) : null}
        <Space style={{ marginTop: 12 }}>
          <DownloadButton onDownload={() => tryDownload(true)} />
        </Space>
      </div>
    );
  }

  modal.destroy();
  const successModal = Modal.success({
    title: 'Đã tạo xong tệp Excel',
    content: renderSuccessContent(),
    okText: 'Đóng',
    closable: false,
    keyboard: false,
    maskClosable: false,
  });

  // Vẫn thử tự tải 1 lần ngay khi xong; bị chặn thì modal vẫn còn nút "Tải xuống".
  // TCR-2500 đợt 2 (finding 7) — trả về KẾT QUẢ thật: chỉ 'downloaded' khi file
  // thực sự tải về máy. Trình duyệt chặn popup (Chrome sau ~40s chờ) hoặc tải
  // lỗi → 'failed' (modal vẫn mở kèm nút "Tải xuống" để user tải tay).
  const downloaded = await tryDownload(false);
  return downloaded ? 'downloaded' : 'failed';
}

// TCR-2500 — map job → (percent, text) cho Progress:
// - Có progress + biết total → "Đã xử lý X / Y dòng".
// - Có progress, không total (total: null) → hiển thị theo pha.
// - Không có progress (job cũ) → processing chung, percent 0.
function describeProgress(job: ExcelExportJobResponse): ProgressView {
  const progress = job.progress;
  if (progress && typeof progress.percent === 'number') {
    const percent = clampPercent(progress.percent);
    if (progress.total != null) {
      return {
        percent,
        text: `Đã xử lý ${progress.processed ?? 0} / ${progress.total} dòng`,
      };
    }
    const phaseText = progress.phase ? PHASE_LABELS[progress.phase] : undefined;
    return { percent, text: phaseText ?? 'Đang xử lý dữ liệu…' };
  }
  return { percent: 0, text: 'Hệ thống đang xử lý dữ liệu, job lớn có thể mất vài phút…' };
}

function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}
